import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Grid, Button, Select, MenuItem, FormControl, InputLabel,
  TableBody, TableCell, TableHead, TableRow, Chip,
  TextField, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  Table, TableContainer, CircularProgress, Alert
} from '@mui/material';
// Using native date input instead of DatePicker for compatibility
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import { downloadWorkbook } from '../../utils/excelExport';
import { landlordReportSheets, reportFilename } from '../../utils/reportWorkbooks';

const LandlordReports = () => {
  const [landlords, setLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [properties, setProperties] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(0);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLandlords();
  }, []);

  useEffect(() => {
    if (selectedLandlord) {
      fetchProperties(selectedLandlord);
    }
  }, [selectedLandlord]);

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data);
    } catch (error) {
      toast.error('Error fetching landlords');
    }
  };

  const fetchProperties = async (landlordId) => {
    try {
      const res = await axios.get(`/landlords/${landlordId}/properties`);
      setProperties(res.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedLandlord) {
      toast.error('Please select a landlord');
      return;
    }

    setLoading(true);
    try {
      let endpoint = '';
      const params = new URLSearchParams();
      
      if (selectedProperty) {
        params.append('propertyId', selectedProperty);
      }

      switch (activeTab) {
        case 0: // Rent Roll
          endpoint = `/landlord-reports/${selectedLandlord}/rent-roll`;
          break;
        case 1: // P&L
          endpoint = `/landlord-reports/${selectedLandlord}/profit-loss`;
          params.append('startDate', startDate.toISOString().split('T')[0]);
          params.append('endDate', endDate.toISOString().split('T')[0]);
          break;
        case 2: // Cash Flow
          endpoint = `/landlord-reports/${selectedLandlord}/cash-flow`;
          params.append('startDate', startDate.toISOString().split('T')[0]);
          params.append('endDate', endDate.toISOString().split('T')[0]);
          break;
        case 3: // Owner Statement
          endpoint = `/landlord-reports/${selectedLandlord}/owner-statement`;
          params.append('startDate', startDate.toISOString().split('T')[0]);
          params.append('endDate', endDate.toISOString().split('T')[0]);
          break;
        case 4: { // Payment Report
          // Read through the payout run for the period: the evaluation somebody is composing
          // (or has finalised), not a bare function of the dates. Opening the tab opens the
          // draft, with this period's expenses deducted in full until somebody says otherwise.
          params.append('landlordId', selectedLandlord);
          params.append('startDate', startDate.toISOString().split('T')[0]);
          params.append('endDate', endDate.toISOString().split('T')[0]);
          const res = await axios.get(`/landlord-payout-runs/current?${params.toString()}`);
          setPayoutRun(res.data.run);
          setReportData(res.data.report);
          return;
        }
        case 5: // Funds Flow
          endpoint = `/landlord-reports/${selectedLandlord}/funds-flow`;
          params.append('startDate', startDate.toISOString().split('T')[0]);
          params.append('endDate', endDate.toISOString().split('T')[0]);
          break;
        default:
          // A tab with no report behind it. Bailing out here rather than falling through
          // to the request below, which with an empty endpoint would fetch the app's own
          // base URL and hand whatever came back to the report renderer.
          toast.error('That report is not available');
          return;
      }

      const res = await axios.get(`${endpoint}?${params.toString()}`);
      setReportData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  // The payout run the Payment Report is being read through. Draft while it is composed,
  // finalised once frozen, paid once a landlord payment names it.
  const [payoutRun, setPayoutRun] = useState(null);
  const [runBusy, setRunBusy] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  // Which expenses come off this payout, and how much of each. Everything the landlord owes
  // is listed -- this period's and anything parked from before -- and only what is ticked is
  // deducted.
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesSaving, setExpensesSaving] = useState(false);
  const [expensePool, setExpensePool] = useState([]);
  // expenseId -> amount string. Present means ticked.
  const [expenseChoice, setExpenseChoice] = useState({});

  // Rent the agency pays over before the tenant has paid it. Opened from the Payment Report
  // tab, because that is the sheet it changes.
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [advanceSaving, setAdvanceSaving] = useState(false);
  const [advanceCandidates, setAdvanceCandidates] = useState([]);
  const [advancePicked, setAdvancePicked] = useState([]);
  // invoiceId -> amount string, for a partial advance. Absent means the whole balance.
  const [advanceAmounts, setAdvanceAmounts] = useState({});
  // Keys that were already on the report when the dialog opened — unticking these cancels them.
  const [advanceInitiallyIncluded, setAdvanceInitiallyIncluded] = useState([]);

  const runIsDraft = payoutRun?.status === 'draft';
  const runIsFinalised = payoutRun?.status === 'finalised';
  const runIsPaid = runIsFinalised && Boolean(payoutRun?.landlordPaymentId);

  const applyRunResponse = (data) => {
    if (data?.run) setPayoutRun(data.run);
    if (data?.report) setReportData(data.report);
  };

  const openExpenses = async () => {
    if (!payoutRun) return;
    setExpensesOpen(true);
    setExpensesLoading(true);
    try {
      const res = await axios.get(`/landlord-payout-runs/${payoutRun.id || payoutRun._id}/expenses`);
      const pool = res.data || [];
      setExpensePool(pool);
      const choice = {};
      pool.forEach((p) => { if (p.selectedAmount > 0) choice[p.expenseId] = String(p.selectedAmount); });
      setExpenseChoice(choice);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load the expenses');
      setExpensePool([]);
    } finally {
      setExpensesLoading(false);
    }
  };

  const toggleExpense = (p) => {
    setExpenseChoice((choice) => {
      const next = { ...choice };
      if (next[p.expenseId] !== undefined) delete next[p.expenseId];
      else next[p.expenseId] = String(p.remaining);
      return next;
    });
  };

  const saveExpenses = async () => {
    setExpensesSaving(true);
    try {
      const selections = Object.entries(expenseChoice).map(([expenseId, amount]) => ({ expenseId, amount: parseFloat(amount) }));
      const res = await axios.put(`/landlord-payout-runs/${payoutRun.id || payoutRun._id}/expenses`, { selections });
      applyRunResponse(res.data);
      setExpensesOpen(false);
      toast.success('Deductions updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save the deductions');
    } finally {
      setExpensesSaving(false);
    }
  };

  const expenseChoiceTotal = Object.values(expenseChoice).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  // Max that can still be taken on this run: undeducted elsewhere, which already includes room
  // for this run's current selection (selectedAmount is not subtracted from remaining).
  const expenseMax = (p) => Math.max(p.remaining || 0, p.selectedAmount || 0);
  const expenseChoiceInvalid = expensePool.some((p) => {
    const v = expenseChoice[p.expenseId];
    if (v === undefined) return false;
    const n = parseFloat(v);
    return !Number.isFinite(n) || n <= 0 || n > expenseMax(p) + 0.005;
  });

  const finaliseRun = async () => {
    if (!payoutRun) return;
    setRunBusy(true);
    try {
      const res = await axios.post(`/landlord-payout-runs/${payoutRun.id || payoutRun._id}/finalise`);
      applyRunResponse(res.data);
      toast.success(res.data.message || 'Payout finalised');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not finalise the payout');
    } finally {
      setRunBusy(false);
    }
  };

  const reopenRun = async () => {
    if (!reopenReason.trim()) { toast.error('Give a reason for reopening'); return; }
    setRunBusy(true);
    try {
      const res = await axios.post(`/landlord-payout-runs/${payoutRun.id || payoutRun._id}/reopen`, { reason: reopenReason.trim() });
      applyRunResponse(res.data);
      setReopenOpen(false);
      setReopenReason('');
      toast.success(res.data.message || 'Payout reopened');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not reopen the payout');
    } finally {
      setRunBusy(false);
    }
  };

  const { formatMoney } = useCurrency();
  const formatCurrency = (amount) => formatMoney(amount, { decimals: 0 });

  const handlePrint = () => {
    window.print();
  };

  // Excel, built from the data already on screen. The PDF is the printed sheet; this is
  // the same figures as columns, for anyone who wants to sort, filter or add them up.
  const handleDownloadExcel = async () => {
    if (!reportData) return;
    const slug = ['rent-roll', 'profit-loss', 'cash-flow', 'owner-statement', 'payment-report', 'funds-flow'][activeTab];
    const landlordName = landlords.find((l) => l._id === selectedLandlord)?.name || null;
    const dated = activeTab !== 0;
    try {
      await downloadWorkbook({
        filename: reportFilename(slug, {
          landlordName,
          startDate: dated ? startDate.toISOString().split('T')[0] : null,
          endDate: dated ? endDate.toISOString().split('T')[0] : null
        }),
        sheets: landlordReportSheets(slug, reportData)
      });
    } catch (error) {
      toast.error('Could not build the Excel file');
      console.error('Excel export failed:', error);
    }
  };

  const advanceCandidateKey = (c) => c.candidateKey || c.invoiceId || (c.tenantId ? `new:${c.tenantId}` : null);

  const openAdvances = async () => {
    setAdvanceOpen(true);
    setAdvanceLoading(true);
    setAdvancePicked([]);
    setAdvanceAmounts({});
    setAdvanceInitiallyIncluded([]);
    try {
      const periodStart = startDate.toISOString().split('T')[0];
      const periodEnd = endDate.toISOString().split('T')[0];
      const params = new URLSearchParams({
        landlordId: selectedLandlord,
        startDate: periodStart,
        endDate: periodEnd
      });
      if (selectedProperty) params.append('propertyId', selectedProperty);

      const runId = payoutRun?.id || payoutRun?._id || null;
      const listParams = new URLSearchParams({
        landlordId: selectedLandlord,
        status: 'outstanding',
        startDate: periodStart,
        endDate: periodEnd
      });

      const [candRes, advRes] = await Promise.all([
        axios.get(`/landlord-advances/candidates?${params.toString()}`),
        axios.get(`/landlord-advances?${listParams.toString()}`)
      ]);

      const candidates = candRes.data || [];
      // Advances already on this draft (or the period, waiting to be adopted) stay visible so
      // they can be unticked. Without that, recording an advance removes it from candidates
      // and there is no way back from the dialog.
      const onReport = (advRes.data || []).filter((a) => {
        if (a.status && a.status !== 'outstanding') return false;
        if (selectedProperty && String(a.propertyId) !== String(selectedProperty)) return false;
        const aRun = a.payoutRunId || a.payoutRun?.id || a.payoutRun?._id || null;
        if (runId) return !aRun || String(aRun) === String(runId);
        return true;
      });

      const includedRows = onReport.map((a) => {
        const id = a.id || a._id;
        const rent = parseFloat(a.rentAmount) || 0;
        const commission = parseFloat(a.commissionAmount) || 0;
        const net = parseFloat(a.netAmount) || (rent - commission);
        return {
          candidateKey: `adv:${id}`,
          advanceId: id,
          alreadyIncluded: true,
          invoiceId: a.invoiceId || a.invoice?.id || null,
          invoiceNumber: a.invoice?.invoiceNumber || a.invoiceNumber || null,
          periodStart: a.periodStart,
          periodEnd: a.periodEnd,
          status: 'on_report',
          tenantId: a.tenantId || a.tenant?.id || null,
          tenantName: a.tenant?.name || a.tenantName || 'Unknown',
          unitId: a.unitId || a.unit?.id || null,
          unitNumber: a.unit?.unitNumber || a.unitNumber || null,
          propertyId: a.propertyId,
          propertyName: a.property?.propertyName || null,
          invoiceAmount: rent,
          paidAmount: 0,
          balance: rent,
          outstanding: rent,
          commission,
          net,
          isNewTenant: false,
          needsInvoice: false
        };
      });

      setAdvanceCandidates([...includedRows, ...candidates]);
      const includedKeys = includedRows.map((r) => r.candidateKey);
      setAdvanceInitiallyIncluded(includedKeys);
      setAdvancePicked(includedKeys);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load unpaid rent');
      setAdvanceCandidates([]);
    } finally {
      setAdvanceLoading(false);
    }
  };

  const togglePicked = (key) => {
    setAdvancePicked((picked) => (picked.includes(key)
      ? picked.filter((id) => id !== key)
      : [...picked, key]));
  };

  const confirmAdvances = async () => {
    setAdvanceSaving(true);
    const periodStart = startDate.toISOString().split('T')[0];
    const periodEnd = endDate.toISOString().split('T')[0];
    let created = 0;
    let removed = 0;
    const failures = [];

    // Unticked rows that were already on the report: cancel so they drop off the sheet.
    for (const key of advanceInitiallyIncluded) {
      if (advancePicked.includes(key)) continue;
      const row = advanceCandidates.find((c) => advanceCandidateKey(c) === key);
      if (!row?.advanceId) continue;
      try {
        await axios.post(`/landlord-advances/${row.advanceId}/cancel`, {
          reason: 'Removed from payment report'
        });
        removed += 1;
      } catch (error) {
        failures.push(error.response?.data?.message || 'could not be removed');
      }
    }

    // Newly ticked candidates: record an advance, one request each so one refusal does not
    // discard the rest of the selection.
    for (const key of advancePicked) {
      const candidate = advanceCandidates.find((c) => advanceCandidateKey(c) === key);
      if (!candidate || candidate.alreadyIncluded) continue;
      try {
        const typed = advanceAmounts[key];
        const amount = typed !== undefined && typed !== '' ? parseFloat(typed) : undefined;
        const body = {
          periodStart,
          periodEnd,
          ...(amount !== undefined ? { amount } : {})
        };
        if (candidate.invoiceId) body.invoiceId = candidate.invoiceId;
        else if (candidate.tenantId) body.tenantId = candidate.tenantId;
        else continue;
        await axios.post('/landlord-advances', body);
        created += 1;
      } catch (error) {
        failures.push(error.response?.data?.message || 'could not be advanced');
      }
    }

    setAdvanceSaving(false);
    setAdvanceOpen(false);
    if (created) toast.success(`${created} advance${created === 1 ? '' : 's'} recorded`);
    if (removed) toast.success(`${removed} advance${removed === 1 ? '' : 's'} removed from the report`);
    failures.forEach((message) => toast.error(message));
    if (created || removed) handleGenerateReport();
  };

  // What a picked candidate will be advanced: the typed amount, else the whole balance. The
  // commission scales with it, the way the server works it out. Rows already on the report
  // keep the amounts that were recorded — they are only kept or cancelled here.
  const advanceFigures = (c) => {
    if (c.alreadyIncluded) {
      return { rent: c.outstanding, commission: c.commission, net: c.net };
    }
    const key = advanceCandidateKey(c);
    const typed = advanceAmounts[key];
    const rent = typed !== undefined && typed !== '' ? parseFloat(typed) || 0 : c.outstanding;
    const rate = c.outstanding > 0 ? c.commission / c.outstanding : 0;
    const commission = Math.round(rent * rate);
    return { rent, commission, net: rent - commission };
  };

  const advanceMax = (c) => (c.balance != null ? c.balance : c.outstanding);

  const advanceAmountInvalid = advanceCandidates
    .filter((c) => !c.alreadyIncluded && advancePicked.includes(advanceCandidateKey(c)))
    .some((c) => {
      const typed = advanceAmounts[advanceCandidateKey(c)];
      if (typed === undefined || typed === '') return false;
      const n = parseFloat(typed);
      return !Number.isFinite(n) || n <= 0 || n > advanceMax(c) + 0.005;
    });

  const advanceTotals = advanceCandidates
    .filter((c) => advancePicked.includes(advanceCandidateKey(c)))
    .reduce((acc, c) => {
      const f = advanceFigures(c);
      return { rent: acc.rent + f.rent, commission: acc.commission + f.commission, net: acc.net + f.net };
    }, { rent: 0, commission: 0, net: 0 });

  const advanceRemovals = advanceInitiallyIncluded.filter((key) => !advancePicked.includes(key)).length;
  const advanceAdditions = advancePicked.filter((key) => {
    const c = advanceCandidates.find((row) => advanceCandidateKey(row) === key);
    return c && !c.alreadyIncluded;
  }).length;
  const advanceDirty = advanceRemovals > 0 || advanceAdditions > 0;

  const handleDownloadPDF = async () => {
    if (!selectedLandlord) {
      toast.error('Please select a landlord');
      return;
    }

    try {
      let endpoint = '';
      const params = new URLSearchParams();
      
      if (selectedProperty) {
        params.append('propertyId', selectedProperty);
      }

      const reportNames = ['rent-roll', 'profit-loss', 'cash-flow', 'owner-statement', 'payment-report', 'funds-flow'];
      endpoint = `/landlord-reports/${selectedLandlord}/${reportNames[activeTab]}/pdf`;

      // Add date params for reports that need them
      if (activeTab !== 0) {
        params.append('startDate', startDate.toISOString().split('T')[0]);
        params.append('endDate', endDate.toISOString().split('T')[0]);
      }
      // The printed payment report is the evaluation on screen, not a bare period.
      if (activeTab === 4 && (payoutRun?.id || payoutRun?._id)) {
        params.append('runId', payoutRun.id || payoutRun._id);
      }

      const response = await axios.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportNames[activeTab]}-${selectedLandlord}-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error downloading PDF');
      console.error('Error downloading PDF:', error);
    }
  };

  const renderRentRoll = () => {
    if (!reportData?.rentRoll) return null;

    return (
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Property</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Lease Start</TableCell>
              <TableCell>Lease End</TableCell>
              <TableCell>Rent Amount</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Last Payment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.rentRoll.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.propertyName}</TableCell>
                <TableCell>{row.unitNumber}</TableCell>
                <TableCell>
                  {row.isVacant ? (
                    <Chip label="Vacant" color="default" size="small" />
                  ) : (
                    row.tenantName
                  )}
                </TableCell>
                <TableCell>
                  {row.leaseStartDate ? new Date(row.leaseStartDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  {row.leaseEndDate ? new Date(row.leaseEndDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>{formatCurrency(row.rentAmount)}</TableCell>
                <TableCell>
                  <Chip
                    label={row.paymentStatus}
                    color={
                      row.paymentStatus === 'paid' ? 'success' :
                      row.paymentStatus === 'overdue' ? 'error' :
                      row.paymentStatus === 'partial' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatCurrency(row.balance)}</TableCell>
                <TableCell>
                  {row.lastPaymentDate ? new Date(row.lastPaymentDate).toLocaleDateString() : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
    );
  };

  const renderProfitAndLoss = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Income</Typography>
              {reportData.income?.categories.map((cat, idx) => (
                <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                  <Typography>{cat.accountName}</Typography>
                  <Typography>{formatCurrency(cat.amount)}</Typography>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1}>
                <Typography variant="subtitle1" fontWeight="bold">Total Income</Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formatCurrency(reportData.income?.total || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Expenses</Typography>
              {reportData.expenses?.categories.map((cat, idx) => (
                <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                  <Typography>{cat.accountName}</Typography>
                  <Typography>{formatCurrency(cat.amount)}</Typography>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1}>
                <Typography variant="subtitle1" fontWeight="bold">Total Expenses</Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formatCurrency(reportData.expenses?.total || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6">Net Operating Income (NOI)</Typography>
            <Typography variant="h6">{formatCurrency(reportData.netOperatingIncome || 0)}</Typography>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderCashFlow = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Opening Cash Balance</Typography>
          <Typography variant="h5">{formatCurrency(reportData.openingCash || 0)}</Typography>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Cash Inflows</Typography>
              {reportData.cashInflows?.categories.map((cat, idx) => (
                <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                  <Typography>{cat.category}</Typography>
                  <Typography color="success.main">{formatCurrency(cat.amount)}</Typography>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1}>
                <Typography variant="subtitle1" fontWeight="bold">Total Inflows</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                  {formatCurrency(reportData.cashInflows?.total || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Cash Outflows</Typography>
              {reportData.cashOutflows?.categories.map((cat, idx) => (
                <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                  <Typography>{cat.category}</Typography>
                  <Typography color="error.main">{formatCurrency(cat.amount)}</Typography>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1}>
                <Typography variant="subtitle1" fontWeight="bold">Total Outflows</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                  {formatCurrency(reportData.cashOutflows?.total || 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="h6">Net Cash Flow</Typography>
            <Typography variant="h6">
              {formatCurrency(reportData.netCashFlow || 0)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6">Closing Cash Balance</Typography>
            <Typography variant="h6">{formatCurrency(reportData.closingCash || 0)}</Typography>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderOwnerStatement = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Summary</Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Total Rent Collected</Typography>
            <Typography>{formatCurrency(reportData.summary?.totalRentCollected || 0)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Total Expenses</Typography>
            <Typography color="error.main">
              {formatCurrency(reportData.summary?.totalExpenses || 0)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Management Fees</Typography>
            <Typography color="error.main">
              {formatCurrency(reportData.summary?.managementFees || 0)}
            </Typography>
          </Box>
          {/* Money moved on or off the account by hand — a transfer to the agency, a
              correction. Without this line such a transfer reduced the payable with
              nothing on the page to say why. */}
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Transfers &amp; Adjustments</Typography>
            <Typography color={(reportData.summary?.adjustments || 0) < 0 ? 'success.main' : 'error.main'}>
              {formatCurrency(reportData.summary?.adjustments || 0)}
            </Typography>
          </Box>
          {/* Earned before settlements. Without this line the three figures above cannot
              reconcile to the payable once any payment has been made to the landlord. */}
          <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1} mb={1}>
            <Typography fontWeight="bold">Net Earned</Typography>
            <Typography fontWeight="bold">
              {formatCurrency(reportData.summary?.netEarned || 0)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Less: Paid to Landlord</Typography>
            <Typography color="error.main">
              {formatCurrency(reportData.summary?.paymentsMade || 0)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop={1}>
            <Typography variant="h6" fontWeight="bold">Net Payout (Amount Payable)</Typography>
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {formatCurrency(reportData.summary?.netPayout || 0)}
            </Typography>
          </Box>
          {reportData.summary?.paymentsNote && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {reportData.summary.paymentsNote}
            </Typography>
          )}
        </Paper>

        {/* The lines behind the totals. The payment report names every deduction; the
            statement used to print totals and stop, so the two sheets about the same month
            did not read alike. Each list here is what its figure above was summed from. */}
        {[
          { title: 'Deductions — expenses', entries: reportData.expenses?.entries, key: 'exp' },
          { title: 'Transfers & adjustments', entries: reportData.adjustments?.entries, key: 'adj',
            note: 'A positive amount is money moved off the landlord\'s account; a negative amount is money put back.' },
          { title: 'Payments made to landlord', entries: reportData.paymentsMade?.entries, key: 'pay' }
        ].filter((s) => s.entries && s.entries.length > 0).map((section) => (
          <Paper key={section.key} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{section.title}</Typography>
            <ScrollTable size="small" maxHeight={320}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Property / Unit</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.entries.map((e, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{e.date ? new Date(e.date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{e.description || '-'}</TableCell>
                    <TableCell>
                      {[e.property && e.property !== 'N/A' ? e.property : null, e.unit ? `Unit ${e.unit}` : null]
                        .filter(Boolean).join(' · ') || '-'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(e.amount || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
            {section.note && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>{section.note}</Typography>
            )}
          </Paper>
        ))}
        {reportData.summary?.adjustmentsNote && (
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            {reportData.summary.adjustmentsNote}
          </Typography>
        )}

        {reportData.propertyBreakdown && reportData.propertyBreakdown.length > 0 && (
          <ScrollTable containerProps={{ component: Paper }}>
              <TableHead>
                <TableRow>
                  <TableCell>Property</TableCell>
                  <TableCell>Income</TableCell>
                  <TableCell>Expenses</TableCell>
                  <TableCell>Management Fees</TableCell>
                  <TableCell>Net Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.propertyBreakdown.map((prop, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{prop.propertyName}</TableCell>
                    <TableCell>{formatCurrency(prop.income)}</TableCell>
                    <TableCell>{formatCurrency(prop.expenses)}</TableCell>
                    <TableCell>{formatCurrency(prop.managementFees)}</TableCell>
                    <TableCell>{formatCurrency(prop.netAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
        )}
      </Box>
    );
  };

  const renderFundsFlow = () => {
    if (!reportData?.lines) return null;

    const money = (n) => (n ? formatCurrency(n) : '');

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Funds flow for {reportData.landlord?.name || ''}
        </Typography>
        {!reportData.account && (
          <Typography color="text.secondary">
            Nothing has been posted to this landlord&apos;s account yet.
          </Typography>
        )}

        <ScrollTable size="small" containerProps={{ component: Paper }}>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>From / To</TableCell>
              <TableCell align="right">Paid out</TableCell>
              <TableCell align="right">Credited</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className="pin-left" colSpan={6} sx={{ fontWeight: 'bold' }}>Opening balance</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(reportData.opening?.balance || 0)}</TableCell>
            </TableRow>

            {reportData.lines.map((line) => (
              <TableRow key={line.lineId} sx={line.reversedByEntryId ? { opacity: 0.6 } : undefined}>
                <TableCell className="pin-left">{line.entryDate ? new Date(line.entryDate).toLocaleDateString() : '-'}</TableCell>
                <TableCell><Chip label={line.kind} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  {line.description}
                  {line.reversedByEntryId && <Chip label="Reversed" size="small" color="warning" sx={{ ml: 1 }} />}
                  {line.reversesEntryId && <Chip label="Reversal" size="small" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{line.counterparty || '-'}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>{money(line.debit)}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>{money(line.credit)}</TableCell>
                <TableCell align="right">{formatCurrency(line.balance || 0)}</TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell className="pin-left" colSpan={4} sx={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>Totals</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>{money(reportData.totals?.debit)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>{money(reportData.totals?.credit)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>{formatCurrency(reportData.closing || 0)}</TableCell>
            </TableRow>
          </TableBody>
        </ScrollTable>

        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Paid out is money leaving the landlord&apos;s account — payments to the landlord,
          commission, expenses, transfers. Credited is rent collected for them. A negative
          balance means the landlord has been paid ahead of collection.
          {reportData.truncated ? ' This period has more lines than fit in one view; narrow the dates to see all of them.' : ''}
        </Typography>
      </Box>
    );
  };

  const renderPaymentReport = () => {
    if (!reportData?.rows) return null;

    const heading = reportData.heading || `PAYMENT REPORT FOR ${(reportData.landlord?.name || '').toUpperCase()}.`;
    const showPropertyGroups = (reportData.propertyGroups || []).length > 1;

    const commissionLabel = reportData.commission?.rateLabel
      ? `LESS ${reportData.commission.rateLabel} COMMISSION`
      : 'LESS COMMISSION';

    // Totals sit in the same table as the units (Bagalu sheet): Suit | Tenant | Rent Payable.
    const summaryCell = { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' };
    const labelCell = { colSpan: 2 };

    const runChip = payoutRun && (
      runIsPaid ? { label: `${payoutRun.runNumber} · Paid`, color: 'success' }
        : runIsFinalised ? { label: `${payoutRun.runNumber} · Finalised`, color: 'primary' }
          : { label: `${payoutRun.runNumber} · Draft`, color: 'default' }
    );

    const tenantCell = (row) => row.tenantLabel
      || (row.isVacant ? 'VACANT' : row.tenantName)
      || '';

    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="h6" gutterBottom sx={{ mb: 0, textTransform: 'uppercase' }}>
            {heading}
          </Typography>
          {runChip && <Chip size="small" label={runChip.label} color={runChip.color} variant={runIsDraft ? 'outlined' : 'filled'} />}
        </Box>
        {showPropertyGroups && reportData.properties?.length > 0 && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {reportData.properties.map((p) => p.propertyName).join(', ')}
          </Typography>
        )}
        {runIsDraft && (
          <Alert severity="info" sx={{ my: 1.5 }} className="no-print">
            This is an evaluation. Choose which expenses to deduct now and which tenants to
            advance, then finalise it to freeze the figures. Expenses left out stay owed and
            can be deducted on a later payout.
          </Alert>
        )}
        {runIsFinalised && !runIsPaid && (
          <Alert severity="success" sx={{ my: 1.5 }} className="no-print">
            Finalised{payoutRun.finalisedBy?.name ? ` by ${payoutRun.finalisedBy.name}` : ''}. Record the
            landlord payment against {payoutRun.runNumber} from Landlord Payments; reopen it here if
            something needs to change first.
          </Alert>
        )}
        {runIsPaid && (
          <Alert severity="success" sx={{ my: 1.5 }} className="no-print">
            Paid{payoutRun.landlordPayment?.paymentNumber ? ` as ${payoutRun.landlordPayment.paymentNumber}` : ''}.
            This sheet is frozen; void the payment to change it.
          </Alert>
        )}

        <ScrollTable containerProps={{ component: Paper }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>SUIT</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>TENANT</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>RENT PAYABLE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(reportData.propertyGroups || []).map((group) => (
              <React.Fragment key={group.propertyId || group.propertyName}>
                {showPropertyGroups && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                      {group.propertyName}
                    </TableCell>
                  </TableRow>
                )}

                {group.rows.map((row, idx) => (
                  <TableRow key={row.unitId || `former-${row.tenantId}-${idx}`}>
                    <TableCell>{row.isFormerTenant ? '' : (row.unitNumber || '')}</TableCell>
                    <TableCell>{tenantCell(row)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.collection || 0)}
                    </TableCell>
                  </TableRow>
                ))}

                {showPropertyGroups && (
                  <TableRow>
                    <TableCell {...labelCell} sx={{ fontWeight: 'bold' }}>
                      {`${group.propertyName} TOTAL`.toUpperCase()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(group.collection)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}

            <TableRow>
              <TableCell {...labelCell} sx={summaryCell}>TOTAL</TableCell>
              <TableCell align="right" sx={summaryCell}>
                {formatCurrency(reportData.totalCollection || 0)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell {...labelCell}>{commissionLabel}</TableCell>
              <TableCell align="right">
                {formatCurrency(reportData.commission?.amount || 0)}
              </TableCell>
            </TableRow>

            {reportData.deductions?.lines?.map((line) => (
              <TableRow key={line.id}>
                <TableCell {...labelCell}>
                  {`LESS ${line.unitNumber ? `${line.unitNumber} - ` : ''}${line.description}`.toUpperCase()}
                  {line.partial && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (part of {formatCurrency(line.expenseAmount)})
                    </Typography>
                  )}
                  {line.fromEarlier && <Chip label="Carried forward" size="small" sx={{ ml: 1 }} className="no-print" />}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(line.amount)}
                </TableCell>
              </TableRow>
            ))}

            {reportData.adjustments?.lines?.map((line) => (
              <TableRow key={line.id}>
                <TableCell {...labelCell}>
                  {`${line.amount < 0 ? 'ADD' : 'LESS'} ${line.description}`.toUpperCase()}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(Math.abs(line.amount))}
                </TableCell>
              </TableRow>
            ))}

            {reportData.advances?.rent > 0 && (
              <TableRow>
                <TableCell {...labelCell}>ADD ADVANCE ON UNPAID RENT</TableCell>
                <TableCell align="right">
                  {formatCurrency(reportData.advances.rent)}
                </TableCell>
              </TableRow>
            )}
            {reportData.advances?.commission > 0 && (
              <TableRow>
                <TableCell {...labelCell}>LESS COMMISSION ON ADVANCE</TableCell>
                <TableCell align="right">
                  {formatCurrency(reportData.advances.commission)}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell {...labelCell} sx={{ ...summaryCell, fontSize: '1.05rem' }}>
                AMOUNT TO BE PAID
              </TableCell>
              <TableCell align="right" sx={{ ...summaryCell, fontSize: '1.05rem', color: 'success.main' }}>
                {formatCurrency(reportData.amountPayable || 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </ScrollTable>

        <Typography variant="caption" color="text.secondary" display="block" mt={1} className="no-print">
          Rent payable is this month&apos;s share (landlords are paid monthly even when a
          tenant paid several months as a lumpsum). Download Excel or PDF exports the full
          report, not only what is visible on screen.
          {reportData.frozen && ' Figures as finalised; later postings do not change this sheet.'}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .printable-content, .printable-content * {
            visibility: visible;
          }
          .printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      <Paper sx={{ p: 3 }} className="printable-content">
        <Typography variant="h4" gutterBottom>Landlord Reports</Typography>
        
        <Box sx={{ mb: 3 }} className="no-print">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Landlord</InputLabel>
                <Select
                  value={selectedLandlord}
                  onChange={(e) => {
                    setSelectedLandlord(e.target.value);
                    setSelectedProperty('');
                  }}
                >
                  {landlords.map((landlord) => (
                    <MenuItem key={landlord._id} value={landlord._id}>
                      {landlord.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Property (Optional)</InputLabel>
                <Select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  disabled={!selectedLandlord}
                >
                  <MenuItem value="">All Properties</MenuItem>
                  {properties.map((property) => (
                    <MenuItem key={property._id} value={property._id}>
                      {property.propertyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="End Date"
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
              >
                Generate
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }} className="no-print">
          <Tabs
            // WRAPPED ONTO AS MANY ROWS AS IT TAKES, not scrolled.
            //
            // The default variant clips whatever does not fit, silently — a report that
            // exists simply is not there. Scrolling fixes the clipping but not the
            // discovery: arrows still hide most of the list from somebody who has never
            // seen this screen. Wrapping costs one row of height and shows every report at
            // once, which is the point of a tab bar.
            sx={{
              '& .MuiTabs-flexContainer': { flexWrap: 'wrap' },
              // The sliding indicator is absolutely positioned along the bottom of the whole
              // control and knows only the selected tab's HORIZONTAL offset, so once the
              // tabs wrap it underlines whatever sits below the selection. The selected tab
              // carries its own underline instead.
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                borderBottom: '2px solid transparent',
                '&.Mui-selected': { borderBottomColor: 'primary.main' }
              }
            }}
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              // Each report has its own shape and they share one `reportData`. Left in
              // place, the next tab renders the last tab's payload through the wrong
              // renderer - which for two reports that both return `landlords` is not a
              // blank screen but a crash.
              setReportData(null);
              setPayoutRun(null);
            }}
          >
            <Tab label="Rent Roll" />
            <Tab label="Profit & Loss" />
            <Tab label="Cash Flow" />
            <Tab label="Owner Statement" />
            <Tab label="Payment Report" />
            <Tab label="Funds Flow" />
          </Tabs>
        </Box>

        {reportData && (
          <Box sx={{ mb: 2 }} className="no-print">
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {/* Only on the Payment Report, which is the only sheet these change. While the
                  run is a draft it can be composed; finalised, it can be reopened until paid. */}
              {activeTab === 4 && runIsDraft && (
                <>
                  <Button variant="outlined" onClick={openExpenses} disabled={runBusy}>
                    Choose expenses
                  </Button>
                  <Button variant="outlined" onClick={openAdvances} disabled={!selectedLandlord || runBusy}>
                    Advance rent / new tenants
                  </Button>
                  <Button variant="contained" color="success" onClick={finaliseRun} disabled={runBusy}>
                    Finalise
                  </Button>
                </>
              )}
              {activeTab === 4 && runIsFinalised && !runIsPaid && (
                <Button variant="outlined" color="warning" onClick={() => { setReopenReason(''); setReopenOpen(true); }} disabled={runBusy}>
                  Reopen
                </Button>
              )}
              <Button startIcon={<PrintIcon />} variant="outlined" onClick={handlePrint}>Print</Button>
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleDownloadPDF}>Download PDF</Button>
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleDownloadExcel}>Download Excel</Button>
            </Box>
          </Box>
        )}

        {reportData && (
          <Box>
            {activeTab === 0 && renderRentRoll()}
            {activeTab === 1 && renderProfitAndLoss()}
            {activeTab === 2 && renderCashFlow()}
            {activeTab === 3 && renderOwnerStatement()}
            {activeTab === 4 && renderPaymentReport()}
            {activeTab === 5 && renderFundsFlow()}
          </Box>
        )}
      </Paper>

      {/* Picking the tenants the agency will cover this month. */}
      <Dialog open={advanceOpen} onClose={() => setAdvanceOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Advance rent / new tenants</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Top up this month&apos;s landlord payout for unpaid or partially paid rent, and for
            new tenants assigned during this period. Tick who to include; untick anyone already
            on the report to remove them. Default for a new pick is this month&apos;s unpaid
            share (or full monthly rent for a new tenant without an invoice yet). The landlord
            receives rent less commission.
          </Alert>

          {advanceLoading && <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}

          {!advanceLoading && advanceCandidates.length === 0 && (
            <Typography color="text.secondary">
              No unpaid, partially paid, new-tenant, or already-advanced rent for this landlord
              in this period.
            </Typography>
          )}

          {!advanceLoading && advanceCandidates.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Unit</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">This month</TableCell>
                    <TableCell align="right">Advance</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Landlord receives</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {advanceCandidates.map((c) => {
                    const key = advanceCandidateKey(c);
                    const picked = advancePicked.includes(key);
                    const figures = advanceFigures(c);
                    const typed = advanceAmounts[key];
                    const maxAmt = advanceMax(c);
                    const bad = !c.alreadyIncluded && picked && typed !== undefined && typed !== ''
                      && (!(parseFloat(typed) > 0) || parseFloat(typed) > maxAmt + 0.005);
                    const statusLabel = c.alreadyIncluded
                      ? 'On report'
                      : (c.isNewTenant
                        ? (c.needsInvoice ? 'New tenant' : `New · ${c.status || 'owing'}`)
                        : (c.status || '—'));
                    return (
                      <TableRow key={key} hover onClick={() => togglePicked(key)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={picked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => togglePicked(key)}
                          />
                        </TableCell>
                        <TableCell>{c.unitNumber || '-'}</TableCell>
                        <TableCell>
                          {c.tenantName}
                          {c.isNewTenant && !c.alreadyIncluded && (
                            <Chip label="New" size="small" color="info" sx={{ ml: 1 }} />
                          )}
                          {c.alreadyIncluded && (
                            <Chip label="Included" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{c.invoiceNumber || (c.needsInvoice ? 'Will raise' : '—')}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{statusLabel}</TableCell>
                        <TableCell align="right">{formatCurrency(c.outstanding)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {c.alreadyIncluded ? (
                            formatCurrency(c.outstanding)
                          ) : (
                            <TextField
                              size="small" type="number" sx={{ width: 130 }}
                              inputProps={{ min: 0, max: maxAmt, step: 1, style: { textAlign: 'right' } }}
                              value={typed ?? ''}
                              placeholder={String(c.outstanding)}
                              disabled={!picked}
                              error={bad}
                              onChange={(e) => setAdvanceAmounts((a) => ({ ...a, [key]: e.target.value }))}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(picked ? figures.commission : c.commission)}</TableCell>
                        <TableCell align="right">{formatCurrency(picked ? figures.net : c.net)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {advancePicked.length > 0 && (
            <Box mt={2} display="flex" justifyContent="flex-end" gap={3}>
              <Typography variant="body2">Rent {formatCurrency(advanceTotals.rent)}</Typography>
              <Typography variant="body2">Commission {formatCurrency(advanceTotals.commission)}</Typography>
              <Typography variant="subtitle2">
                Landlord receives {formatCurrency(advanceTotals.net)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmAdvances}
            disabled={!advanceDirty || advanceSaving || advanceAmountInvalid}
          >
            {advanceSaving
              ? 'Updating…'
              : (
                [
                  advanceAdditions ? `Add ${advanceAdditions}` : null,
                  advanceRemovals ? `Remove ${advanceRemovals}` : null
                ].filter(Boolean).join(' · ') || 'Update advances'
              )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Choosing which expenses come off this payout, and how much of each. */}
      <Dialog open={expensesOpen} onClose={() => setExpensesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose expenses to deduct</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Everything the landlord owes is listed, including expenses carried forward from
            earlier payouts. Tick what comes off this payout; untick to remove a deduction you
            already chose. If only part is to be taken now, type the amount. Whatever is left
            stays owed and is offered again next time.
          </Alert>

          {expensesLoading && <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>}

          {!expensesLoading && expensePool.length === 0 && (
            <Typography color="text.secondary">
              No landlord expenses are waiting to be deducted.
            </Typography>
          )}

          {!expensesLoading && expensePool.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Date</TableCell>
                    <TableCell>Expense</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Still owed</TableCell>
                    <TableCell align="right">Deduct now</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expensePool.map((p) => {
                    const ticked = expenseChoice[p.expenseId] !== undefined;
                    const typed = expenseChoice[p.expenseId];
                    const n = parseFloat(typed);
                    const maxAmt = expenseMax(p);
                    const bad = ticked && (!(n > 0) || n > maxAmt + 0.005);
                    return (
                      <TableRow key={p.expenseId} hover onClick={() => toggleExpense(p)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={ticked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleExpense(p)}
                          />
                        </TableCell>
                        <TableCell>{p.date ? new Date(p.date).toLocaleDateString('en-GB') : '-'}</TableCell>
                        <TableCell>
                          {p.unitNumber ? `${p.unitNumber} - ` : ''}{p.description}
                          {p.propertyName && (reportData?.properties?.length || 0) > 1 && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {p.propertyName}
                            </Typography>
                          )}
                          {p.fromEarlier && <Chip label="Carried forward" size="small" sx={{ ml: 1 }} />}
                          {p.selectedAmount > 0 && (
                            <Chip label="On report" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                          )}
                          {p.appliedElsewhere > 0 && (
                            <Chip label={`${formatCurrency(p.appliedElsewhere)} already deducted`} size="small" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.remaining)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small" type="number" sx={{ width: 140 }}
                            inputProps={{ min: 0, max: maxAmt, step: 1, style: { textAlign: 'right' } }}
                            value={ticked ? typed : ''}
                            disabled={!ticked}
                            error={bad}
                            onChange={(e) => setExpenseChoice((c) => ({ ...c, [p.expenseId]: e.target.value }))}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {expensePool.length > 0 && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Typography variant="subtitle2">
                {Object.keys(expenseChoice).length === 0
                  ? 'No expenses will be deducted on this payout'
                  : `Deducting ${formatCurrency(expenseChoiceTotal)} on this payout`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpensesOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveExpenses}
            disabled={expensesSaving || expensesLoading || expenseChoiceInvalid}
          >
            {expensesSaving ? 'Saving…' : 'Save deductions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopening a finalised run, with the reason on record. */}
      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reopen {payoutRun?.runNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The figures will be recalculated from the ledger as it stands now, and the sheet
            can be changed again until it is finalised afresh.
          </Typography>
          <TextField
            fullWidth autoFocus multiline rows={2} label="Reason"
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={reopenRun} disabled={runBusy || !reopenReason.trim()}>
            Reopen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LandlordReports;

